import React, { useEffect, useMemo, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { State } from '@hookstate/core'
import { Validation } from '@hookstate/validation'
import { getElementFieldValue, makeSetHandler, makeValidationHandler } from '../../globalStates/utils'
import { loadProductProperties, RefProductProperties } from "../../globalStates/ReferenceDataState"
import { getActiveLanguage } from "../../globalStates/LanguageState"
import { Properties } from "../../globalStates/ExhibitorDataState"
import SelectField from '../../components/Field/Select'
import TextField from "../../components/Field/Text"
import Section from "../../components/Section"
import branding from "../../branding/branding"

function buildPropertyOptions(refProperties: any) {
    return refProperties.map((refProperty: { value: any; name: any }) => ({ id: refProperty.value, label: refProperty.name, value: refProperty.value }))
}

function parseEnumProperies(productProperties: Array<any>): RefProductProperties[] {
    return productProperties.map((productProperty, index) => ({
        name: productProperty.name || "",
        value: productProperty.id || "",
    }))
}

function getPropertyName(activeLanguage: string, property: State<Properties, Validation>) {
    return activeLanguage === "en" ? getElementFieldValue(property, "name") : getElementFieldValue(property, "nameDe")
}

function getPropertyUnit(activeLanguage: string, property: State<Properties, Validation>) {
    return activeLanguage === "en" ? getElementFieldValue(property, "unit") : getElementFieldValue(property, "unitDe")
}

function unitIsPresent(activeLanguage: string, property: State<Properties, Validation>) {
    return getPropertyUnit(activeLanguage, property) != null ? getPropertyUnit(activeLanguage, property) : ""
}

function getPropertyTitle(activeLanguage: string, property: State<Properties, Validation>) {
    const propertyName = getPropertyName(activeLanguage, property)
    const propertyUnit = unitIsPresent(activeLanguage, property)

    return `${propertyName} ${propertyUnit ? "|" : ""} ${propertyUnit}`
}

interface ProductInformationProps {
    index: number
    property: State<Properties, Validation>
}

const ProductInformation: React.FC<ProductInformationProps> = ({ property }) => {
    const [enumProperties, setEnumProperties] = useState([])
    const parseProductProperties = parseEnumProperies(enumProperties)
    const enumProductPropertyOptions = useMemo(() => buildPropertyOptions(parseProductProperties), [parseProductProperties])
    const activeLanguage = getActiveLanguage()
    const topic = branding.configuration.topicName
    const propertyId = getElementFieldValue(property, "id")

    useEffect(() => {
        const productPropertiesPromise = loadProductProperties(topic, propertyId, null)
        productPropertiesPromise
            .then(response => setEnumProperties(response.productPropertyEnums))
    }, [propertyId, topic])

    return (
        <>
            <Section
                title={`${getPropertyTitle(activeLanguage, property)}`}
                entityState={property}
            >
                {(property.type.get() === "TEXT" || property.type.get() === "NUMERIC") &&
                    <Row>
                        <Col>
                            <TextField
                                id={propertyId}
                                name="val"
                                label={branding.configuration.productsPage.sctProduct.lblPropertyValue}
                                placeholder={branding.configuration.productsPage.sctProduct.propertyValuePlaceholder}
                                limit={45}
                                validate={makeValidationHandler(property, "val")}
                                value={getElementFieldValue(property, "val")}
                                onChange={makeSetHandler(property, "val")}
                            />
                        </Col>
                    </Row>
                }
                {(property.type.get() === "ENUM") &&
                    <Row>
                        <Col>
                            <SelectField
                                id={propertyId}
                                name="val"
                                label={"Value"}
                                placeholder={"Value"}
                                options={enumProductPropertyOptions}
                                validate={makeValidationHandler(property, 'val')}
                                value={getElementFieldValue(property, "val")}
                                onChange={makeSetHandler(property, 'val')}
                            />
                        </Col>
                    </Row>
                }
            </Section>
        </>
    )
}

export default ProductInformation
